import { useEffect } from "react";

import { useParams } from "@tanstack/react-router";
import { useDispatch } from "react-redux";

import type { CaseSchema } from "@api/generated/types.gen";

import { useAssessmentByCaseNumber } from "@hooks/queries/use-assessments";
import { useCaseByCaseNumber } from "@hooks/queries/use-cases";

import { showError } from "@utils/notification";
import { cn } from "@utils/ui";

import {
  ASSESSMENT_SECTIONS,
  ASSESSMENT_STATUS,
  AssessmentSection,
  SECTIONS_BY_SERVICE_TYPE,
} from "../constants/sections";
import { isEmptySectionContent } from "../utils/emptySection";

/**
 * Resolves the section list for a case: a valid service-type list wins;
 * otherwise fall back to the legacy template-order sections; otherwise empty.
 * `pinLast` sections (Assessment Notes) always render last, once — drop any
 * the resolved list already contributed, then append them (MDRS-136).
 */
export const resolveSections = (
  caseData: Pick<CaseSchema, "service_type" | "template"> | undefined
): AssessmentSection[] => {
  let sections: AssessmentSection[] = [];

  // Service-type-based sections take priority over legacy template-based ones.
  if (caseData?.service_type) {
    const ids = SECTIONS_BY_SERVICE_TYPE[caseData.service_type];
    if (ids?.length) {
      sections = ids
        .map((id) => ASSESSMENT_SECTIONS.find((s) => s.id === id))
        .filter((s): s is AssessmentSection => Boolean(s));
    }
  }
  // Fallback: legacy template-order-based sections.
  if (sections.length === 0 && caseData?.template?.order) {
    sections = ASSESSMENT_SECTIONS.filter((s) =>
      s.templateIndex?.includes(caseData.template!.order)
    );
  }

  const pinned = ASSESSMENT_SECTIONS.filter((s) => s.pinLast);
  const pinnedIds = new Set(pinned.map((s) => s.id));
  const rest = sections.filter((s) => !pinnedIds.has(s.id));
  return [...rest, ...pinned];
};

type TabLinksProps = {
  selectedId: string;
  handleFeatureItemClick: (id: string) => void;
};
export const TabLinks = ({ selectedId, handleFeatureItemClick }: TabLinksProps) => {
  const dispatch = useDispatch();
  const { id: caseNumber } = useParams({ strict: false }) as { id: string };

  const {
    data: caseData,
    isLoading: isCaseLoading,
    error: caseError,
  } = useCaseByCaseNumber({
    case_number: caseNumber || "",
    enabled: !!caseNumber,
  });

  const { data: assessmentData, isLoading: isAssessmentLoading } =
    useAssessmentByCaseNumber({
      caseNumber: caseNumber || "",
      enabled: !!caseNumber,
    });

  // Auto-export runs once for this case in AudioTranscript (the content
  // pane) — this component (the sidebar) is always mounted alongside it, so
  // a second instance here would just be a duplicate concurrent poller.

  useEffect(() => {
    if (!caseNumber) {
      showError("No case selected", dispatch);
      return;
    }
  }, [caseNumber, dispatch]);

  useEffect(() => {
    if (caseError) {
      showError("Error fetching case details", dispatch);
    }
  }, [caseError, dispatch]);

  const renderTabs = () => {
    if (isCaseLoading || isAssessmentLoading) {
      return <LoadingSkeleton />;
    }

    const sections = resolveSections(caseData);

    if (sections.length === 0) {
      return (
        <div className="text-foreground-muted">
          No sections available for this case
        </div>
      );
    }

    return (
      <>
        {sections.map((item) => {
          let isCompleted = false;
          let isExported = false;
          let isEmpty = false;

          if (assessmentData && item.key) {
            const field = assessmentData[item.key as keyof typeof assessmentData];
            // Assessment Notes (key "notes") is a plain string, not a
            // {content, status} field — it has no status to accept/export. Tick
            // it off once notes have been entered, mirroring how summarised
            // sections show completion (MDRS-161).
            if (item.key === "notes") {
              isCompleted = typeof field === "string" && field.trim().length > 0;
            } else if (field && typeof field === "object" && "status" in field) {
              // Check if the field has status information
              isExported =
                field.status === ASSESSMENT_STATUS.ACCEPTED ||
                field.status === ASSESSMENT_STATUS.EXPORTED;
              isEmpty = "content" in field && isEmptySectionContent(field.content);
            } else if (typeof field === "string") {
              // Older plain-string shape
              isEmpty = isEmptySectionContent(field);
            }
          }

          return (
            <TabItem
              key={item.id}
              id={item.id}
              title={item.title}
              isActive={selectedId === item.id}
              onClick={handleFeatureItemClick}
              isCompleted={isCompleted}
              isExported={isExported}
              isEmpty={isEmpty}
            />
          );
        })}
      </>
    );
  };

  return (
    <div className="flex h-full w-full flex-col gap-1 overflow-y-auto rounded-[10px] border border-border bg-background-white p-2">
      {renderTabs()}
    </div>
  );
};

/** Visual state of a section row's leading status indicator (MDRS-114, MDRS-576). */
type SectionStatus = "completed" | "incomplete" | "empty";

type TabItemProps = {
  id: string;
  title: string;
  isActive: boolean;
  isCompleted?: boolean;
  isExported?: boolean;
  isEmpty?: boolean;
  onClick: (id: string) => void;
};

const TabItem = ({
  id,
  title,
  isActive,
  isCompleted,
  isExported,
  isEmpty,
  onClick,
}: TabItemProps) => {
  // Completion is independent of selection — the indicator icon reflects
  // exported/completed state only, so it doesn't flip when a completed
  // section is opened. `isActive` drives the row's own highlight instead
  // (MDRS-114).
  const status: SectionStatus = isEmpty
    ? "empty"
    : isCompleted || isExported
      ? "completed"
      : "incomplete";

  return (
    <button
      key={id}
      type="button"
      data-section-id={id}
      data-status={status}
      aria-current={isActive ? "true" : undefined}
      className={cn(
        "flex w-full items-center gap-2.5 rounded-lg px-4 py-[11px] text-left transition-colors",
        // Montserrat, sized down from text-xs's 12px to 11px — same
        // compensation ratio the shared Table primitive uses (14px→13px) to
        // offset Montserrat's larger x-height at a given font-size.
        "font-head text-[11px] text-foreground",
        "cursor-pointer hover:bg-primary-50/60",
        isActive && "pointer-events-none bg-primary-50 font-semibold text-primary"
      )}
      onClick={() => onClick(id)}
    >
      <SectionStatusIcon status={status} />
      <span className="min-w-0 flex-1 truncate">{title}</span>
    </button>
  );
};

const SectionStatusIcon = ({ status }: { status: SectionStatus }) => {
  if (status === "empty") {
    return (
      <span
        role="img"
        aria-label="No information found"
        title="No information found in the transcript for this section"
        className="flex size-4 shrink-0 items-center justify-center rounded-full bg-warning-600 text-[9px] font-bold text-black"
      >
        !
      </span>
    );
  }

  if (status === "completed") {
    return (
      <span
        aria-hidden="false"
        className="flex size-4 shrink-0 items-center justify-center rounded-full bg-success-600 text-[8px] font-bold text-white"
      >
        ✓
      </span>
    );
  }

  return (
    <span
      className="size-4 shrink-0 rounded-full border-[1.5px] border-border bg-background-white"
      aria-hidden="true"
    />
  );
};

const LoadingSkeleton = () => (
  <>
    {Array(5)
      .fill(0)
      .map((_, index) => (
        <div
          key={index}
          className="h-8 animate-pulse rounded-md bg-dark-100/40 dark:bg-dark-50/30"
        />
      ))}
  </>
);
